import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import {
  Users, TrendingUp, Building2, DollarSign, Home, MessageCircle, CalendarClock,
  Bot, PhoneCall, UserCheck, Ban, SendHorizonal, Flame, CalendarCheck, Clock,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import api from '../services/api';
import StatCard from '../components/shared/StatCard';
import ReagendarVisitaModal from '../components/shared/ReagendarVisitaModal';

const COLORS = ['#1B3F5E', '#4A8FBF', '#F5A623', '#28A745', '#DC3545', '#6f42c1', '#fd7e14'];

const ORIGEM_LABELS = {
  meta_ads: 'Meta Ads', google_ads: 'Google Ads', chaves_na_mao: 'Chaves na Mão',
  site: 'Site', indicacao: 'Indicação', ligacao: 'Ligação', whatsapp: 'WhatsApp',
  visita_espontanea: 'Visita', olx: 'OLX', sub100: 'Sub100', outro: 'Outro',
};

function MiniCard({ label, value, icon: Icon, color = 'blue', onClick }) {
  const colors = {
    blue: 'bg-blue-50 text-blue-700 border-blue-100',
    green: 'bg-green-50 text-green-700 border-green-100',
    orange: 'bg-orange-50 text-orange-700 border-orange-100',
    red: 'bg-red-50 text-red-700 border-red-100',
    purple: 'bg-purple-50 text-purple-700 border-purple-100',
    teal: 'bg-teal-50 text-teal-700 border-teal-100',
  };
  return (
    <div
      onClick={onClick}
      className={`rounded-xl border p-4 flex flex-col gap-2 ${colors[color]} ${onClick ? 'cursor-pointer hover:brightness-95' : ''}`}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium opacity-80">{label}</span>
        <Icon size={16} className="opacity-60" />
      </div>
      <p className="text-2xl font-bold">{value ?? '—'}</p>
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [reagendando, setReagendando] = useState(null);

  const { data: statsData } = useQuery({ queryKey: ['dashboard-stats'], queryFn: () => api.get('/dashboard/stats').then(r => r.data.data) });
  const { data: diasData } = useQuery({ queryKey: ['leads-por-dia'], queryFn: () => api.get('/dashboard/leads-por-dia').then(r => r.data.data) });
  const { data: origemData } = useQuery({ queryKey: ['por-origem'], queryFn: () => api.get('/dashboard/por-origem').then(r => r.data.data) });
  const { data: corretoresData } = useQuery({ queryKey: ['top-corretores'], queryFn: () => api.get('/dashboard/top-corretores').then(r => r.data.data) });
  const { data: empsData } = useQuery({ queryKey: ['por-empreendimento'], queryFn: () => api.get('/dashboard/por-empreendimento').then(r => r.data.data) });
  const { data: marketingData } = useQuery({ queryKey: ['marketing-resumo'], queryFn: () => api.get('/marketing/resumo').then(r => r.data.data), refetchInterval: 60000 });
  const { data: botData } = useQuery({ queryKey: ['bot-stats'], queryFn: () => api.get('/dashboard/bot-stats').then(r => r.data.data), refetchInterval: 60000 });
  const { data: visitasData } = useQuery({ queryKey: ['visitas-proximas'], queryFn: () => api.get('/dashboard/visitas-proximas').then(r => r.data.data), refetchInterval: 120000 });

  const stats = statsData || {};
  const bot = botData || {};

  // Helper defensivo para garantir arrays
  const asArray = (x) => Array.isArray(x) ? x : [];

  // Extrair visitas do objeto retornado por /dashboard/visitas-proximas
  const visitasObj = visitasData || {};
  const visitasHoje = Array.isArray(visitasObj.hoje) ? visitasObj.hoje : [];
  const visitasFuturas = Array.isArray(visitasObj.futuras) ? visitasObj.futuras : [];
  const todasVisitas = [...visitasHoje, ...visitasFuturas];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-800">Dashboard</h1>

      {/* KPIs principais */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard title="Leads (30 dias)" value={stats.total_leads_30d ?? '—'} icon={Users} color="primary" />
        <StatCard title="Leads hoje" value={stats.leads_hoje ?? '—'} icon={TrendingUp} color="secondary" />
        <StatCard title="Taxa de conversão" value={stats.taxa_conversao ? `${stats.taxa_conversao}%` : '—'} icon={TrendingUp} color="success" />
        <StatCard title="VGV Disponível" value={stats.vgv_disponivel ? `R$ ${(stats.vgv_disponivel / 1e6).toFixed(1)}M` : '—'} icon={DollarSign} color="warning" />
        <StatCard title="Unidades disponíveis" value={stats.unidades_disponiveis ?? '—'} icon={Home} color="primary" />
      </div>

      {/* Automação IA */}
      <div>
        <h2 className="text-base font-semibold text-gray-600 mb-3 flex items-center gap-2">
          <Bot size={16} /> Automação IA
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <MiniCard label="Msgs recebidas hoje" value={bot.msgs_hoje} icon={MessageCircle} color="blue" onClick={() => navigate('/automacoes')} />
          <MiniCard label="Msgs últimos 7 dias" value={bot.msgs_7d} icon={MessageCircle} color="blue" onClick={() => navigate('/automacoes')} />
          <MiniCard label="Conversas ativas (7d)" value={bot.convs_ativas} icon={Bot} color="teal" onClick={() => navigate('/automacoes')} />
          <MiniCard label="Aguardando humano" value={bot.aguardando_humano} icon={UserCheck} color={parseInt(bot.aguardando_humano) > 0 ? 'orange' : 'green'} onClick={() => navigate('/automacoes')} />
          <MiniCard label="SDR qualificou (mês)" value={bot.leads_qualificados_mes} icon={PhoneCall} color="purple" onClick={() => navigate('/automacoes')} />
          <MiniCard label="Reaquecidos (mês)" value={bot.reaquecidos_mes} icon={Flame} color="orange" onClick={() => navigate('/leads-reaquecidos')} />
        </div>
      </div>

      {/* Visitas & Comunicações */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Próximas visitas */}
        <div className="bg-white rounded-xl shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
              <CalendarCheck size={18} className="text-green-600" /> Próximas Visitas
            </h2>
            <div className="flex gap-3 text-sm">
              <span className="bg-green-50 text-green-700 px-2 py-0.5 rounded-full font-medium">
                {bot.visitas_hoje ?? 0} hoje
              </span>
              <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                {bot.visitas_futuras ?? 0} futuras
              </span>
            </div>
          </div>
          {asArray(todasVisitas).length === 0 ? (
            <p className="text-gray-400 text-sm py-4 text-center">Nenhuma visita agendada</p>
          ) : (
            <div className="space-y-2">
              {asArray(todasVisitas).slice(0, 10).map(v => (
                <div key={v.id} className="flex items-start justify-between py-2 border-b border-gray-50 last:border-0">
                  <div>
                    <p className="text-sm font-medium text-gray-800">{v.attendee_nome || v.titulo}</p>
                    <p className="text-xs text-gray-500">{v.empreendimento || '—'}</p>
                  </div>
                  <div className="text-right flex-shrink-0 ml-4">
                    <p className="text-xs font-semibold text-blue-700">
                      {format(new Date(v.start_time), "dd/MM", { locale: ptBR })}
                    </p>
                    <p className="text-xs text-gray-500">
                      {format(new Date(v.start_time), "HH:mm")}
                    </p>
                    {v.cal_booking_id && (
                      <button
                        onClick={() => setReagendando(v)}
                        className="mt-1 text-xs text-blue-600 hover:underline"
                      >
                        Reagendar
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Comunicações em massa */}
        <div className="bg-white rounded-xl shadow p-6">
          <h2 className="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <SendHorizonal size={18} className="text-blue-600" /> Comunicações
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <div onClick={() => navigate('/envios-massa')} className="cursor-pointer p-4 bg-blue-50 rounded-xl hover:bg-blue-100 transition-colors">
              <p className="text-2xl font-bold text-blue-700">{bot.campanhas_enviadas ?? 0}</p>
              <p className="text-xs text-gray-500 mt-1">Campanhas enviadas (30d)</p>
            </div>
            <div onClick={() => navigate('/envios-massa')} className="cursor-pointer p-4 bg-indigo-50 rounded-xl hover:bg-indigo-100 transition-colors">
              <p className="text-2xl font-bold text-indigo-700">{bot.total_msgs_enviadas ?? 0}</p>
              <p className="text-xs text-gray-500 mt-1">Mensagens enviadas (30d)</p>
            </div>
            <div onClick={() => navigate('/blacklist')} className="cursor-pointer p-4 bg-red-50 rounded-xl hover:bg-red-100 transition-colors">
              <div className="flex items-center gap-2">
                <Ban size={16} className="text-red-500" />
                <p className="text-2xl font-bold text-red-600">{bot.total_blacklist ?? 0}</p>
              </div>
              <p className="text-xs text-gray-500 mt-1">Números na blacklist</p>
            </div>
            <div onClick={() => navigate('/agendamentos')} className="cursor-pointer p-4 bg-purple-50 rounded-xl hover:bg-purple-100 transition-colors">
              <div className="flex items-center gap-2">
                <Clock size={16} className="text-purple-600" />
                <p className="text-2xl font-bold text-purple-700">{bot.msgs_agendadas_pendentes ?? 0}</p>
              </div>
              <p className="text-xs text-gray-500 mt-1">Msgs agendadas pendentes</p>
            </div>
          </div>
        </div>
      </div>

      {/* Gráficos principais */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow p-6">
          <h2 className="text-lg font-semibold text-gray-700 mb-4">Leads por Dia (30 dias)</h2>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={asArray(diasData)}>
              <XAxis dataKey="dia" tickFormatter={d => { try { return format(new Date(String(d).substring(0, 10) + 'T00:00:00'), 'dd/MM', { locale: ptBR }); } catch { return d; } }} tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip labelFormatter={d => { try { return format(new Date(String(d).substring(0, 10) + 'T00:00:00'), 'dd/MM/yyyy', { locale: ptBR }); } catch { return d; } }} />
              <Bar dataKey="total" fill="#1B3F5E" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-xl shadow p-6">
          <h2 className="text-lg font-semibold text-gray-700 mb-4">Leads por Canal</h2>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={asArray(origemData).map(o => ({ ...o, name: ORIGEM_LABELS[o.origem] || o.origem, value: parseInt(o.total) }))} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                {asArray(origemData).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Corretores */}
        <div className="bg-white rounded-xl shadow p-6">
          <h2 className="text-lg font-semibold text-gray-700 mb-4">Top Corretores do Mês</h2>
          <div className="space-y-3">
            {asArray(corretoresData).map((c, i) => (
              <div key={c.id} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                <div className="flex items-center gap-3">
                  <span className="w-7 h-7 bg-primary text-white text-xs rounded-full flex items-center justify-center font-bold">{i + 1}</span>
                  <span className="font-medium text-gray-700">{c.nome}</span>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-gray-800">{c.vendas} vendas</p>
                  <p className="text-xs text-gray-400">{c.total_leads} leads</p>
                </div>
              </div>
            ))}
            {!corretoresData?.length && <p className="text-gray-400 text-sm">Nenhum dado disponível</p>}
          </div>
        </div>

        {/* Empreendimentos */}
        <div className="bg-white rounded-xl shadow p-6">
          <h2 className="text-lg font-semibold text-gray-700 mb-4">Performance por Empreendimento</h2>
          <div className="space-y-4">
            {asArray(empsData).filter(e => e.nome).map(e => {
              const total = parseInt(e.total || 1);
              const disp = parseInt(e.disponivel || 0);
              const pct = total > 0 ? Math.round(((total - disp) / total) * 100) : 0;
              return (
                <div key={e.id}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="font-medium text-gray-700">{e.nome}</span>
                    <span className="text-gray-500">{pct}% vendido</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-success h-2 rounded-full transition-all" style={{ width: `${pct}%` }} />
                  </div>
                  <p className="text-xs text-gray-400 mt-1">{e.total_leads} leads</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Marketing */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div onClick={() => navigate('/marketing/chatwoot')} className="bg-white rounded-xl shadow p-6 cursor-pointer hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <MessageCircle size={20} className="text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-700">Chatwoot</h2>
              <p className="text-xs text-gray-400">Atendimento ao cliente</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center p-3 bg-blue-50 rounded-lg">
              <p className="text-2xl font-bold text-blue-700">{marketingData?.chatwoot?.conversas_abertas ?? '—'}</p>
              <p className="text-xs text-gray-500 mt-1">Conversas abertas</p>
            </div>
            <div className="text-center p-3 bg-orange-50 rounded-lg">
              <p className="text-2xl font-bold text-orange-600">{marketingData?.chatwoot?.nao_atendidas ?? '—'}</p>
              <p className="text-xs text-gray-500 mt-1">Não atendidas</p>
            </div>
          </div>
        </div>

        <div onClick={() => navigate('/marketing/postiz')} className="bg-white rounded-xl shadow p-6 cursor-pointer hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <CalendarClock size={20} className="text-purple-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-700">Postiz</h2>
              <p className="text-xs text-gray-400">Agendamento de posts</p>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4">
            <div className="text-center p-3 bg-purple-50 rounded-lg">
              <p className="text-2xl font-bold text-purple-700">{marketingData?.postiz?.posts_agendados ?? '—'}</p>
              <p className="text-xs text-gray-500 mt-1">Posts agendados</p>
            </div>
          </div>
        </div>
      </div>

      <ReagendarVisitaModal
        isOpen={!!reagendando}
        onClose={() => setReagendando(null)}
        onSuccess={() => {
          setReagendando(null);
          queryClient.invalidateQueries({ queryKey: ['visitas-proximas'] });
        }}
        bookingId={reagendando?.cal_booking_id}
        nome={reagendando?.attendee_nome || reagendando?.titulo}
        startTime={reagendando?.start_time}
        empreendimento={reagendando?.empreendimento}
      />
    </div>
  );
}
